import React, { useCallback, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import type { ColDef, ValueFormatterParams, CellClickedEvent, CellClassParams, CellStyle } from 'ag-grid-community';
import type { AgGridReact } from 'ag-grid-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload } from '@fortawesome/free-solid-svg-icons';
import type { InsyncModelWidgetInstanceConfig, InsyncModelData } from './types';
import { useDataProviderWidget } from '../../../blocks/hooks';
import { setSelectedKpi, getSelectedKpi, subscribeKpi } from '../../../blocks/hooks/kpiSelectionStore';
import { WidgetLoadingState, WidgetErrorState, WidgetEmptyState, WidgetPreviewBanner } from '../../../design';
import { AgGridWrapper } from '../../../blocks/tables';

// Preview (no asset): a small annual slice so the tile renders standalone.
const SAMPLE_DATA: InsyncModelData = {
  ticker: 'NVDA',
  model: [
    { line: 'Total Revenue', '2024': 60922, '2025': 130497, '2026': 206000, '2027': 268000 },
    { line: 'Gross Profit', '2024': 44301, '2025': 97900, '2026': 154000, '2027': 200000 },
    { line: 'Operating Income', '2024': 32972, '2025': 81453, '2026': 132000, '2027': 172000 },
    { line: 'Net Income', '2024': 29760, '2025': 72880, '2026': 118000, '2027': 154000 },
    { line: 'EPS', '2024': 1.19, '2025': 2.94, '2026': 4.8, '2027': 6.25 },
  ],
};

interface Props {
  config: InsyncModelWidgetInstanceConfig;
  dashboardData?: Record<string, unknown>;
  readOnly?: boolean;
  widgetInstanceId?: string;
}

interface PeriodCol {
  field: string;
  isAnnual: boolean;
  est: boolean;
}

const NOW = new Date();
const CUR_Y = NOW.getFullYear();
const CUR_Q = Math.floor(NOW.getMonth() / 3) + 1;

// Model period columns: "2026" (annual) / "3Q26" (quarter). A period is an
// estimate once it's current-or-forward (no reported actual yet).
const parsePeriodCol = (col: string): PeriodCol | null => {
  if (/^\d{4}$/.test(col)) return { field: col, isAnnual: true, est: +col >= CUR_Y };
  const m = /^([1-4])Q(\d{2})$/.exec(col);
  if (m) {
    const q = +m[1];
    const y = 2000 + +m[2];
    return { field: col, isAnnual: false, est: y > CUR_Y || (y === CUR_Y && q >= CUR_Q) };
  }
  return null;
};

const fmtNum = (v: unknown): string => {
  if (v == null || v === '') return '';
  const n = Number(v);
  if (!Number.isFinite(n)) return String(v);
  return n.toLocaleString('en-US', { maximumFractionDigits: Math.abs(n) < 100 ? 2 : 0 });
};

// Line items that have a consensus-revision series → clicking one drives the
// "Stock Price vs Consensus Revision" chart. The model may label EPS either way.
const KPI_BY_LINE: Record<string, string> = {
  'Total Revenue': 'Total Revenue',
  'Gross Profit': 'Gross Profit',
  'EBIT': 'EBIT',
  'EBITDA': 'EBITDA',
  'Net Income': 'Net Income',
  'Diluted EPS': 'Diluted EPS',
  'EPS': 'Diluted EPS',
};

const InsyncModelWidgetRenderer: React.FC<Props> = ({ config, dashboardData = {}, widgetInstanceId }) => {
  const uid = dashboardData?.assetDataUid as string | undefined;
  const isPreview = !uid;

  const { data, loading, error, refetch } = useDataProviderWidget<InsyncModelData>(
    'insync_model',
    uid,
    config as unknown as Record<string, unknown>,
    { enabled: !!uid, widgetInstanceId },
  );

  const displayData = uid ? data : SAMPLE_DATA;
  const ticker = displayData?.ticker || 'Asset';
  const rows = useMemo(() => displayData?.model ?? [], [displayData]);

  // Cross-widget: clicking a KPI line item drives the Consensus Revision chart.
  const selectedKpi = useSyncExternalStore(subscribeKpi, () => getSelectedKpi(uid));
  const onCellClicked = useCallback((e: CellClickedEvent) => {
    if (!uid || e.colDef.field !== 'line') return;
    const kpi = KPI_BY_LINE[String((e.data as Record<string, unknown>)?.line ?? '')];
    if (kpi) setSelectedKpi(uid, kpi);
  }, [uid]);

  const { annualCols, quarterCols } = useMemo(() => {
    if (!rows.length) return { annualCols: [] as PeriodCol[], quarterCols: [] as PeriodCol[] };
    const periods = Object.keys(rows[0]).map(parsePeriodCol).filter((p): p is PeriodCol => p !== null);
    return {
      annualCols: periods.filter((p) => p.isAnnual),
      quarterCols: periods.filter((p) => !p.isAnnual),
    };
  }, [rows]);

  const hasQuarters = quarterCols.length > 0;
  const [view, setView] = useState<'Quarterly' | 'Annual'>('Annual');
  const activeView = hasQuarters ? view : 'Annual';
  const visibleCols = activeView === 'Quarterly' ? quarterCols : annualCols;

  const gridRef = useRef<AgGridReact>(null);
  const handleDownload = () => {
    gridRef.current?.api?.exportDataAsCsv({
      fileName: `${ticker}_InSync_Model_${activeView}.csv`,
      processCellCallback: (p) => p.value, // raw values (clean for re-import), not the comma-formatted display
    });
  };

  const columnDefs = useMemo<ColDef[]>(() => {
    const cols: ColDef[] = [
      { headerName: 'Line Item', field: 'line', width: 230, minWidth: 150, pinned: 'left',
        sortable: false, filter: false, cellClass: 'font-medium', tooltipField: 'line',
        // KPI rows are clickable to drive the consensus-revision chart.
        cellStyle: (p: CellClassParams): CellStyle | null => {
          const kpi = KPI_BY_LINE[String((p.data as Record<string, unknown>)?.line ?? '')];
          if (!kpi) return null;
          const style: CellStyle = { cursor: 'pointer' };
          if (kpi === selectedKpi) {
            style.fontWeight = 700;
            style.boxShadow = 'inset 3px 0 0 #2563eb';
            style.background = 'rgba(37,99,235,0.06)';
          }
          return style;
        },
      },
    ];
    for (const p of visibleCols) {
      cols.push({
        headerName: p.field,
        field: p.field,
        headerClass: p.est ? 'text-primary-600' : undefined,
        sortable: false,
        filter: false,
        type: 'rightAligned',
        cellClass: 'tabular-nums',
        flex: 1,
        minWidth: 72,
        valueFormatter: (pp: ValueFormatterParams) => fmtNum(pp.value),
      });
    }
    return cols;
  }, [visibleCols, selectedKpi]);

  const defaultColDef = useMemo<ColDef>(() => ({ sortable: false, filter: false, resizable: true }), []);

  if (loading) return <WidgetLoadingState size="sm" />;
  if (error) return <WidgetErrorState message={error} onRetry={refetch} />;
  if (uid && !rows.length) {
    return <WidgetEmptyState title="No model" description="No InSync model is available for this security." />;
  }
  if (!rows.length) return null;

  return (
    <div className={`h-full flex flex-col overflow-hidden rounded-xl border border-neutral-200 bg-surface shadow-sm ${isPreview ? 'opacity-70' : ''}`}>
      {isPreview && <WidgetPreviewBanner />}
      <div className="flex items-center gap-3 px-3 py-2 border-b border-neutral-100 shrink-0">
        <span className="text-sm font-semibold text-neutral-800">InSync Model</span>
        <span className="text-[11px] text-neutral-400 uppercase tracking-wide">{ticker} · {activeView === 'Quarterly' ? 'quarterly' : 'annual'} ($M)</span>
        <div className="ml-auto flex items-center gap-2">
          {hasQuarters && (
            <div className="flex items-center gap-1 rounded-xl border border-neutral-200 bg-neutral-50 p-1">
              {(['Quarterly', 'Annual'] as const).map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setView(v)}
                  className={`rounded-lg px-2.5 py-1 text-[11px] font-semibold transition-colors ${
                    v === activeView
                      ? 'bg-primary-600 text-white shadow-sm'
                      : 'text-neutral-500 hover:bg-neutral-200 hover:text-neutral-800'
                  }`}
                >
                  {v}
                </button>
              ))}
            </div>
          )}
          <button
            type="button"
            onClick={handleDownload}
            title="Download CSV"
            aria-label="Download CSV"
            className="p-1.5 rounded-lg text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 transition-colors"
          >
            <FontAwesomeIcon icon={faDownload} className="text-xs" />
          </button>
        </div>
      </div>
      <div className="flex-1 overflow-hidden">
        <AgGridWrapper
          ref={gridRef}
          containerClassName="ag-theme-alpine ag-compact h-full w-full"
          columnDefs={columnDefs}
          rowData={rows}
          defaultColDef={defaultColDef}
          onCellClicked={onCellClicked}
          rowHeight={30}
          headerHeight={28}
          suppressCellFocus
        />
      </div>
      <div className="shrink-0 px-3 pb-1.5 pt-0.5 flex items-center justify-between">
        <span className="text-[9px] uppercase tracking-wide text-neutral-400">USD Mn except per-share · blue = estimate</span>
        <span className="text-[9px] uppercase tracking-wide text-neutral-400">Source: InSync</span>
      </div>
    </div>
  );
};

export default InsyncModelWidgetRenderer;
